import React, {useState} from "react";
import {Box} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useGridApiRef} from "@mui/x-data-grid-pro";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import emptyTransactions from "../../../assets/lists/empty_transactions.svg";
import {TEmailTemplate} from "../../../interfaces/email";
import ListSnackbar, {TAlert} from "../../../patterns/list/listSnackbar";
import List from "../../../patterns/list/list";
import {actionColumn, addErrorAlert, addSuccessAlert, columnDefinition, ColumnType, gridAction} from "../../../patterns/list/listUtils";
import {createEmailTemplate, getCompanyEmailTemplates} from "../../../services/AdminService";
import {manageStringError, RequestOrder} from "../../../common/methods/ApiService";
import {toComponentPayloads} from "../componentPayload";

const PREFIX = "email_templates.other_operations";

type TOtherOperationTemplate = TEmailTemplate & {operationName: string};

export default function ListOtherOperationsEmailTemplates(props: {operationId: string, setReloadParent: (reload: boolean) => void}): JSX.Element {
  const {operationId, setReloadParent} = props;
  const {t} = useTranslation();
  const apiRef = useGridApiRef();

  const [reload, setReload] = useState(false);
  const [alerts, setAlerts] = useState<TAlert[]>([]);
  const [importing, setImporting] = useState(false);

  const importTemplate = (template: TOtherOperationTemplate): void => {
    setImporting(true);
    createEmailTemplate({
      templateType: template.templateType,
      mailjetId: template.mailjetId,
      operation: "bo/operations/" + operationId,
      name: template.name,
      subject: template.subject ?? null,
      mailSender: template.mailSender ?? null,
      extraVariables: template.extraVariables,
      components: toComponentPayloads(template.components),
      newPointAdded: false,
      newParticipantAdded: false,
      beginOperationAdded: false,
    })
      .then(() => {
        addSuccessAlert(t(`${PREFIX}.actions.import_success`), setAlerts);
        setReloadParent(true);
      })
      .catch(error => manageStringError(error, error => addErrorAlert(error, setAlerts), t))
      .finally(() => setImporting(false));
  };

  const columns = [
    columnDefinition(t, PREFIX, "operationName", 220),
    columnDefinition(t, PREFIX, "name", 240),
    columnDefinition(t, PREFIX, "subject", 280),
    columnDefinition(t, PREFIX, "updatedAt", 180, ColumnType.DATE, true, true),
    actionColumn(params => [
      gridAction(params, "import", t(`${PREFIX}.actions.import`), ContentCopyIcon, importTemplate, false, importing, false)
    ])
  ];

  const loadTemplates = (page: number, maxItems: number, search: string, order: RequestOrder): Promise<{totalItems: number, items: TOtherOperationTemplate[]}> => {
    return getCompanyEmailTemplates(operationId).then(templates => {
      const rows: TOtherOperationTemplate[] = templates.map(template => ({
        ...template,
        operationName: template.operation?.name ?? ""
      }));

      const term = search ? search.trim().toLowerCase() : "";
      const filtered = term === ""
        ? rows
        : rows.filter(row => `${row.operationName} ${row.name} ${row.subject ?? ""}`.toLowerCase().includes(term));

      if (order) {
        const way = order.sort === "desc" ? -1 : 1;
        filtered.sort((a, b) => way * String(a[order.field as keyof TOtherOperationTemplate] ?? "")
          .localeCompare(String(b[order.field as keyof TOtherOperationTemplate] ?? "")));
      }

      // List envoie une page 1-indexée (cf. patterns/list/list.tsx : loadRows(page + 1, ...))
      const firstItem = Math.max(page - 1, 0) * maxItems;

      return {
        totalItems: filtered.length,
        items: filtered.slice(firstItem, firstItem + maxItems)
      };
    });
  };

  return (
    <Box sx={{height: "80vh", p: 7}}>
      <ListSnackbar alerts={alerts} setAlerts={setAlerts} />
      <List
        enableSelection={false}
        translationPrefix={PREFIX}
        columns={columns}
        apiRef={apiRef}
        loadRows={loadTemplates}
        height="70vh"
        reload={reload}
        setReload={setReload}
        defaultOrder={{field: "operationName", sort: "asc"}}
        emptyListIcon={emptyTransactions}
        sx={{width: "100%"}}
        enableToolbarSearch={true}
      />
    </Box>
  );
}
